import { ReactElement, useState, useRef } from "react";
import { useEventListener } from "../../../../hooks/UseEventListener";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext";
import { ComponentService } from "../../../../services/http/component/ComponentService";
import { SearchComponentsPaginatedParameters } from "../../../../services/http/component/models/SearchComponentsPaginatedParameters";
import { ISearchComponentsPaginatedResult } from "../../../../services/http/component/models/ISearchComponentsPaginatedResult";
import { IBranch } from "../../../../services/http/component/models/IBranch";
import { className } from "../../../../shared/helpers";
import SearchResult from "./SearchResult";
import styles from "./Search.module.scss";

interface SearchProps
{
    searchVisible: boolean;
    setSearchVisibility: (state: boolean) => void;
}

const Search = (props: SearchProps): ReactElement =>
{
    const searchRef = useRef<HTMLDivElement>(null as any);
    const lastSearchRef = useRef<string>("");
    const [searchText, setSearchText] = useState<string>("");
    const keyUpTimeout = useRef<number>(null as any);
    const inputRef = useRef<HTMLInputElement>(null as any);
    const [searchResult, setSearchResult] = useState<ISearchComponentsPaginatedResult>(null as any);
    const service = useDependency(ComponentService);
    const timeMachine = useTimeMachine();
    
    const handleInputKeyUp = () =>
    {
        clearTimeout(keyUpTimeout.current);

        keyUpTimeout.current = window.setTimeout(async () =>
        {
            const text = inputRef.current?.value?.trim();
            setSearchText(text);
            lastSearchRef.current = text;
            if (!text || text.length < 1)
            {
                setSearchResult(null as any);
                return;
            }

            const parameters = new SearchComponentsPaginatedParameters();

            Object.assign(parameters, {
                text: text,
                sortBy: 'Name',
                sortOrder: 0,
                pageSize: 10,
                pageNumber: 1,
                date: timeMachine
            });

            const results = await service.search(parameters);
            setSearchResult(results as any);
        }, 500);
    }

    const clearSearchParams = () =>
    {
        props.setSearchVisibility(false);
        setSearchResult(null as any);
        clearTimeout(keyUpTimeout.current);
    };

    const handleClickOutside = (e: PointerEvent) =>
    {
        const target = e.target as HTMLElement;

        if (searchRef?.current && !searchRef.current.contains(target))
            clearSearchParams();

    };

    const getBranches = (branchId: number): IBranch[] =>
    {
        return searchResult?.branches.filter(x => x.branchId === branchId) ?? [];
    };

    function reset(): void
    {
        if (lastSearchRef.current !== "") {
            setSearchResult(null as any);
            lastSearchRef.current = "";
        };

        setSearchText("");
        inputRef.current.value = "";
    }

    useEventListener("pointerdown", handleClickOutside);

    return (
        <div
            ref={searchRef}
            {...className({ [styles.inputVisible]: props.searchVisible })}
        >
            {props.searchVisible && (
                <div className={styles.search} ref={searchRef}>
                    <div className={styles.inputWrapper}>
                        <input
                            ref={inputRef}
                            type="text"
                            id="searchText"
                            name="searchText"
                            placeholder="Search..."
                            autoComplete="off"
                            onKeyUp={handleInputKeyUp}
                            autoFocus
                        />

                        <i {...className("material-icons", styles.searchIcon)}>search</i>

                        {(searchText !== "" || searchText !== lastSearchRef.current) && (
                            <button
                                className={styles.resetBtn}
                                onClick={reset}
                            >
                                <i className="material-icons">close</i>
                            </button>
                        )}
                        {searchResult && (
                            <SearchResult
                                text={inputRef.current?.value}
                                components={searchResult.components}
                                getBranches={getBranches}
                                onSelected={clearSearchParams}
                            />
                        )}
                    </div>
                </div>
            )}   

            {!props.searchVisible && (
                <button onClick={() => props.setSearchVisibility(!props.searchVisible)}>
                    <i className="material-icons">search</i>
                </button>
            )}
        </div>
    );
};

export default Search;
